/**
 * Client-side only storage for authentication tokens using cookies
 * This avoids SSR cookie access that would break Cloudflare caching
 * Cookies are used for cross-domain sharing, but accessed only on client
 *
 * `token` and `x-client` are shared with websale on the COOKIES_DOMAIN — it is
 * literally the same cookie, not a copy. They must therefore be written with an
 * explicit maxAge; without it they become session cookies and this app would
 * overwrite websale's persistent ones on every write, logging the user out on
 * browser close even though BE still holds a valid session (CIN-681).
 */

import { COOKIES_NAMES } from "~/utils/constants.js";
import { useClientCookie } from "~/composables/useClientCookie.js";

export function useAuthStorage() {
  const config = useRuntimeConfig();

  const cookieOptions = {
    domain: config.public.COOKIES_DOMAIN,
    path: "/",
    maxAge: 60 * 60 * 24 * 30, // 30 days, matching REFRESH_TOKEN validity on BE
    sameSite: "lax",
    secure: true,
  };

  const tokenClientCookie = useClientCookie(COOKIES_NAMES.token, cookieOptions);
  const uuidClientCookie = useClientCookie(
    COOKIES_NAMES.clientUUID,
    cookieOptions,
  );
  const profilePictureClientCookie = useClientCookie(
    COOKIES_NAMES.profilePicture,
    cookieOptions,
  );
  const refClientCookie = useClientCookie(COOKIES_NAMES.ref, cookieOptions);

  const token = useState("client_auth_token", () => null);
  const clientUUID = useState("client_auth_uuid", () => null);
  const profilePicture = useState("client_profile_picture", () => null);
  const ref = useState("client_ref", () => null);
  const isHydrated = useState("auth_storage_hydrated", () => false);

  /**
   * Initialize storage from cookies (client-side only)
   */
  const initializeFromStorage = () => {
    if (import.meta.server || isHydrated.value) return;
    if (typeof document === "undefined") return;

    isHydrated.value = true;

    const storedToken = tokenClientCookie.getCookie();
    const storedUUID = uuidClientCookie.getCookie();
    const storedProfilePicture = profilePictureClientCookie.getCookie();
    const storedRef = refClientCookie.getCookie();

    if (storedToken) token.value = storedToken;
    if (storedUUID) clientUUID.value = storedUUID;
    if (storedProfilePicture) profilePicture.value = storedProfilePicture;
    if (storedRef) ref.value = storedRef;
  };

  /**
   * Set token and persist to cookie
   */
  const setToken = (newToken) => {
    token.value = newToken;
    if (import.meta.client) {
      if (newToken) {
        tokenClientCookie.setCookie(newToken);
      } else {
        tokenClientCookie.deleteCookie();
      }
    }
  };

  /**
   * Set clientUUID and persist to cookie
   */
  const setClientUUID = (newUUID) => {
    clientUUID.value = newUUID;
    if (import.meta.client) {
      if (newUUID) {
        uuidClientCookie.setCookie(newUUID);
      } else {
        uuidClientCookie.deleteCookie();
      }
    }
  };

  /**
   * Set profilePicture and persist to cookie
   */
  const setProfilePicture = (newProfilePicture) => {
    profilePicture.value = newProfilePicture;
    if (import.meta.client) {
      if (newProfilePicture) {
        profilePictureClientCookie.setCookie(newProfilePicture);
      } else {
        profilePictureClientCookie.deleteCookie();
      }
    }
  };

  /**
   * Set ref and persist to cookie
   * @param {string|null} newRef - Referrer value or null to clear
   */
  const setRef = (newRef) => {
    ref.value = newRef;
    if (import.meta.client) {
      if (newRef) {
        refClientCookie.setCookie(newRef);
      } else {
        refClientCookie.deleteCookie();
      }
    }
  };

  /**
   * Clear all auth data
   */
  const clearAuth = () => {
    setToken(null);
    setClientUUID(null);
    setProfilePicture(null);
  };

  /**
   * Get current token value
   */
  const getToken = () => {
    if (!isHydrated.value) {
      initializeFromStorage();
    }
    return token.value;
  };

  /**
   * Get current clientUUID value
   */
  const getClientUUID = () => {
    if (!isHydrated.value) {
      initializeFromStorage();
    }
    return clientUUID.value;
  };

  /**
   * Get current profilePicture value
   */
  const getProfilePicture = () => {
    if (!isHydrated.value) {
      initializeFromStorage();
    }
    return profilePicture.value;
  };

  /**
   * Get current ref value
   */
  const getRef = () => {
    if (!isHydrated.value) {
      initializeFromStorage();
    }
    return ref.value;
  };

  return {
    token: computed(() => token.value),
    clientUUID: computed(() => clientUUID.value),
    profilePicture: computed(() => profilePicture.value),
    ref: computed(() => ref.value),
    setToken,
    setClientUUID,
    setProfilePicture,
    setRef,
    clearAuth,
    getToken,
    getClientUUID,
    getProfilePicture,
    getRef,
    initializeFromStorage,
  };
}
